import { useState, useCallback, useRef } from 'react';
import { authService } from '@/services/authService';
import { SUBSIDIARY_CODE } from '@/config';
import { useCampaignStore } from '@/stores/campaignStore';

export interface UploadState {
  progress: number;
  uploading: boolean;
  complete: boolean;
  error: string | null;
  stalled: boolean;
  fileKey: string | null;
  fileName: string | null;
}

const INITIAL_STATE: UploadState = {
  progress: 0,
  uploading: false,
  complete: false,
  error: null,
  stalled: false,
  fileKey: null,
  fileName: null,
};

export function useFileUpload() {
  const [state, setState] = useState<UploadState>(INITIAL_STATE);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const lastProgressRef = useRef<{ time: number; loaded: number } | null>(null);
  const stallTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileRef = useRef<File | null>(null);

  const clearStallTimer = useCallback(() => {
    if (stallTimerRef.current) {
      clearInterval(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  }, []);

  const upload = useCallback(
    (file: File, uploadUrl: string, fileKey: string, presigned = true): Promise<void> => {
      fileRef.current = file;

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;
        lastProgressRef.current = null;
        setState((s) => ({
          ...s,
          uploading: true,
          complete: false,
          error: null,
          stalled: false,
          progress: 0,
          fileKey,
          fileName: file.name,
        }));

        // Stall detection: check every 10s if progress hasn't moved in 30s
        stallTimerRef.current = setInterval(() => {
          const last = lastProgressRef.current;
          if (last && Date.now() - last.time > 30_000) {
            setState((s) => ({ ...s, stalled: true }));
          }
        }, 10_000);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setState((s) => ({ ...s, progress: pct, stalled: false }));
            lastProgressRef.current = { time: Date.now(), loaded: e.loaded };
          }
        };

        xhr.onload = () => {
          clearStallTimer();
          if (xhr.status >= 200 && xhr.status < 300) {
            setState((s) => ({
              ...s,
              uploading: false,
              complete: true,
              progress: 100,
            }));
            resolve();
          } else {
            const err = `Upload failed (${xhr.status})`;
            setState((s) => ({ ...s, uploading: false, error: err }));
            saveErrorBreadcrumb(file, fileKey, err);
            reject(new Error(err));
          }
        };

        xhr.onerror = () => {
          clearStallTimer();
          const err = 'Upload failed — check your network connection';
          setState((s) => ({ ...s, uploading: false, error: err }));
          saveErrorBreadcrumb(file, fileKey, err);
          reject(new Error('Network error'));
        };

        xhr.onabort = () => {
          clearStallTimer();
          setState(INITIAL_STATE);
        };

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', 'text/csv');

        if (presigned) {
          // Presigned URLs have auth baked into query string.
          // DO NOT add Authorization header — Spaces rejects with SignatureDoesNotMatch.
        } else {
          // Local dev upload goes to the API server — needs auth + tenant headers
          const token = authService.getToken();
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          const { subsidiaryCode, selectedCampaign } = useCampaignStore.getState();
          xhr.setRequestHeader('X-Subsidiary', subsidiaryCode || SUBSIDIARY_CODE);
          if (selectedCampaign?.id) xhr.setRequestHeader('X-Campaign-Id', selectedCampaign.id);
        }

        xhr.send(file);
      });
    },
    [clearStallTimer],
  );

  const abort = useCallback(() => {
    xhrRef.current?.abort();
    clearStallTimer();
    setState(INITIAL_STATE);
    fileRef.current = null;
  }, [clearStallTimer]);

  const reset = useCallback(() => {
    abort();
  }, [abort]);

  /**
   * Re-upload the same file with a fresh presigned URL.
   * The caller is responsible for obtaining the new URL (presigned URLs expire).
   * Returns undefined if no file is stored (i.e., the user never selected one).
   */
  const retry = useCallback(
    (uploadUrl: string, fileKey: string, presigned = true): Promise<void> | undefined => {
      if (!fileRef.current) return undefined;
      return upload(fileRef.current, uploadUrl, fileKey, presigned);
    },
    [upload],
  );

  return {
    ...state,
    file: fileRef.current,
    upload,
    abort,
    reset,
    retry,
  };
}

function saveErrorBreadcrumb(file: File, fileKey: string, error: string) {
  try {
    localStorage.setItem(
      'lastImportAttempt',
      JSON.stringify({
        fileName: file.name,
        fileSize: file.size,
        fileKey,
        error,
        failedAt: new Date().toISOString(),
      }),
    );
  } catch {
    // localStorage may be full or unavailable
  }
}
